import { useState } from "react"

import { isSipAddress } from "@/utils/persona/phoneNumberUtils"

type ConnectionMode = "phone" | "sip"

interface UseConnectionModeOptions {
  /** Current phone number / SIP address value backing the field. */
  phoneNumber: string
  /** Clears (or otherwise updates) the backing value when the mode changes. */
  onPhoneNumberChange: (value: string) => void
  /**
   * Optional side effect run after the mode changes, receiving the newly
   * selected mode. Used by the multi-phone variant to reset wideband audio
   * when switching back to a phone number.
   */
  onModeChange?: (mode: ConnectionMode) => void
}

interface UseConnectionModeResult {
  /**
   * The mode the field should render in. A value that is a SIP address always
   * forces "sip"; otherwise the user's last explicit selection wins, falling
   * back to "phone" for a non-empty plain number.
   */
  inputMode: ConnectionMode
  /** Switches mode, clears the backing value, and runs `onModeChange`. */
  handleModeChange: (key: string) => void
}

/**
 * Drives the phone-vs-SIP connection-type toggle shared by the inbound-voice
 * field groups. Tracks the user's explicit selection while letting an existing
 * SIP-address value force "sip" mode, and clears the backing value on switch.
 */
export function useConnectionMode({
  phoneNumber,
  onPhoneNumberChange,
  onModeChange,
}: UseConnectionModeOptions): UseConnectionModeResult {
  const [userSelectedMode, setUserSelectedMode] = useState<ConnectionMode>(
    isSipAddress(phoneNumber) ? "sip" : "phone",
  )

  const inputMode: ConnectionMode = phoneNumber
    ? isSipAddress(phoneNumber)
      ? "sip"
      : userSelectedMode === "sip"
        ? "sip"
        : "phone"
    : userSelectedMode

  const handleModeChange = (key: string) => {
    const newMode = key as ConnectionMode
    setUserSelectedMode(newMode)
    onPhoneNumberChange("")
    onModeChange?.(newMode)
  }

  return { inputMode, handleModeChange }
}
